import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Rocket,
  Sparkles,
  Search,
  Bot,
  MessageCircle,
  BookOpen,
  BarChart3,
  LayoutDashboard,
  CheckCircle2,
  ArrowRight,
  Zap,
  Globe,
  MapPin,
  Users,
  TrendingUp,
} from "lucide-react";
import SEO from "@/components/SEO";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

function Section({ children, id, className = "" }) {
  return (
    <section id={id} className={`py-20 md:py-28 ${className}`}>
      <div className="max-w-4xl mx-auto px-6">{children}</div>
    </section>
  );
}

function Chip({ children, tone = "primary" }) {
  const tones = {
    primary: "bg-brand-primary/8 text-brand-primary",
    success: "bg-emerald-100 text-emerald-800",
    accent: "bg-amber-100 text-amber-800",
  };
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium tracking-wide uppercase ${tones[tone]}`}>
      {children}
    </span>
  );
}

function Bullet({ children }) {
  return (
    <li className="flex items-start gap-3">
      <CheckCircle2 className="h-5 w-5 text-brand-primary shrink-0 mt-0.5" />
      <span className="text-brand-ink/90 leading-relaxed">{children}</span>
    </li>
  );
}

function PillarCard({ icon: Icon, title, children, index }) {
  return (
    <motion.article
      variants={fadeUp}
      custom={index}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: "-80px" }}
      className="card-soft p-7 md:p-8 relative overflow-hidden group"
    >
      <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-brand-primary/8 text-brand-primary mb-5">
        <Icon className="h-5 w-5" />
      </div>
      <h3 className="font-outfit text-xl md:text-2xl font-semibold text-brand-primary">{title}</h3>
      <div className="mt-4 text-brand-ink/85 leading-relaxed space-y-3">{children}</div>
    </motion.article>
  );
}

export default function PresentationAlbertoPage() {
  return (
    <div className="min-h-screen bg-brand-bg text-brand-ink">
      <SEO
        title="Club Amistad · Resumen ejecutivo para Alberto"
        description="Todo lo que hemos construido para el lanzamiento de las campañas de captación."
        path="/presentacion-alberto"
        noindex
      />

      {/* ---------- HERO ---------- */}
      <header className="relative overflow-hidden bg-gradient-to-b from-brand-primary/[0.04] via-brand-bg to-brand-bg border-b border-brand-line">
        <div className="max-w-4xl mx-auto px-6 pt-16 md:pt-24 pb-20 md:pb-28">
          <motion.div initial="hidden" animate="show" variants={fadeUp}>
            <img src="/club-amistad-logo.png" alt="Club Amistad" className="h-14 md:h-16 w-auto mb-8" />
          </motion.div>

          <motion.div initial="hidden" animate="show" variants={fadeUp} custom={1}>
            <p className="overline mb-4">Resumen ejecutivo · Para Alberto</p>
          </motion.div>

          <motion.h1
            initial="hidden"
            animate="show"
            variants={fadeUp}
            custom={2}
            className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-semibold text-brand-primary leading-[1.05] tracking-tight"
          >
            Todo listo para lanzar.
            <br />
            <span className="text-brand-accent">Aquí tienes el mapa.</span>
          </motion.h1>

          <motion.p
            initial="hidden"
            animate="show"
            variants={fadeUp}
            custom={3}
            className="mt-6 text-lg md:text-xl text-brand-ink/80 leading-relaxed max-w-2xl"
          >
            Un recorrido rápido por lo que hemos construido en la nueva web de Club Amistad y cómo va a
            convertir tu inversión en publicidad en familias reservando viajes.
          </motion.p>

          {/* Estado de campañas */}
          <motion.div
            initial="hidden"
            animate="show"
            variants={fadeUp}
            custom={4}
            className="mt-10 grid sm:grid-cols-2 gap-4"
          >
            <div className="card-soft p-5 border-emerald-200 bg-emerald-50/60">
              <div className="flex items-center gap-2 mb-2">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
                </span>
                <Chip tone="success">En marcha</Chip>
              </div>
              <p className="font-outfit text-lg font-semibold text-emerald-900">Google Ads lanzada</p>
              <p className="text-sm text-emerald-800/80 mt-1">Ya está captando tráfico cualificado hacia la web.</p>
            </div>
            <div className="card-soft p-5 border-amber-200 bg-amber-50/60">
              <div className="flex items-center gap-2 mb-2">
                <Rocket className="h-4 w-4 text-amber-700" />
                <Chip tone="accent">Mañana · 1 sept</Chip>
              </div>
              <p className="font-outfit text-lg font-semibold text-amber-900">Meta se lanza mañana</p>
              <p className="text-sm text-amber-800/80 mt-1">Facebook + Instagram con las creatividades acordadas.</p>
            </div>
          </motion.div>
        </div>

        {/* Bloques decorativos */}
        <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-brand-primary/[0.05] blur-3xl pointer-events-none" />
        <div className="absolute -bottom-40 -left-32 h-96 w-96 rounded-full bg-brand-accent/[0.06] blur-3xl pointer-events-none" />
      </header>

      {/* ---------- 1. WEB NUEVA ---------- */}
      <Section id="web">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}>
          <Chip>01 · La web</Chip>
          <h2 className="font-outfit text-3xl md:text-4xl font-semibold text-brand-primary mt-4 mb-6">
            Una web nueva pensada para familias
          </h2>
          <p className="text-brand-ink/85 leading-relaxed text-lg">
            Hemos hecho la web nueva de arriba abajo, respetando tu logo y la esencia de Club Amistad, pero
            con un aire mucho más moderno y cercano. Cuando un padre o una madre entra en la web, tiene que
            sentir enseguida que aquí les entendemos: que sabemos que planificar un viaje con niños agobia,
            que da miedo equivocarse, y que nosotros se lo ponemos fácil.
          </p>
          <ul className="mt-8 space-y-4">
            <Bullet>
              <strong>10 viajes de la temporada</strong> publicados, cada uno con su propia página, fotos,
              hotel, lo que incluye y su PDF descargable con el dossier completo.
            </Bullet>
            <Bullet>
              Doble acción en cada viaje: <strong>pedir una llamada</strong> o <strong>reservar online</strong>.
            </Bullet>
            <Bullet>Dosier PDF por viaje descargable.</Bullet>
            <Bullet>
              <strong>Reseñas reales de Google</strong> (100+ opiniones, 4,7 estrellas) integradas en la home
              para dar confianza desde el primer segundo.
            </Bullet>
            <Bullet>
              Botón fijo de <strong>llamada directa</strong> en móvil con horario visible.
            </Bullet>
            <Bullet>Páginas legales y aviso de cookies según normativa vigente.</Bullet>
          </ul>
        </motion.div>
      </Section>

      {/* ---------- 2. ASISTENTE IA ---------- */}
      <Section id="asistente" className="bg-white/60 border-y border-brand-line">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}>
          <Chip>02 · Asistente 24/7</Chip>
          <h2 className="font-outfit text-3xl md:text-4xl font-semibold text-brand-primary mt-4 mb-6">
            Un asistente que responde por ti las 24 horas
          </h2>
          <p className="text-brand-ink/85 leading-relaxed text-lg">
            Hemos configurado un asistente inteligente propio de Club Amistad, entrenado con tu tono y con
            la información de tus viajes. Atiende a los visitantes las 24 horas del día, responde en segundos
            las dudas típicas (fechas, precios, hotel, edades de los niños, qué incluye…) y, cuando detecta
            que la familia está interesada, les invita a pedir la llamada del asesor o rellenar el formulario.
          </p>

          <div className="grid md:grid-cols-2 gap-5 mt-8">
            <div className="card-soft p-6">
              <TrendingUp className="h-6 w-6 text-brand-primary mb-3" />
              <h3 className="font-outfit text-lg font-semibold text-brand-primary">Menos leads que se enfrían</h3>
              <p className="text-sm text-brand-ink/80 mt-2 leading-relaxed">
                Muchas familias abandonan la web si no encuentran una respuesta rápida. El asistente evita
                que se vayan sin dejar rastro.
              </p>
            </div>
            <div className="card-soft p-6">
              <Users className="h-6 w-6 text-brand-primary mb-3" />
              <h3 className="font-outfit text-lg font-semibold text-brand-primary">Leads más maduros</h3>
              <p className="text-sm text-brand-ink/80 mt-2 leading-relaxed">
                Cuando llegan a tu equipo, ya vienen con menos dudas básicas: se cierran más rápido.
              </p>
            </div>
          </div>

          <p className="text-brand-muted italic mt-6">
            Especialmente valioso los fines de semana y por las noches, que es cuando las familias planifican
            y la oficina está cerrada.
          </p>
        </motion.div>
      </Section>

      {/* ---------- 3. SEO + GEO ---------- */}
      <Section id="seo">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}>
          <Chip>03 · Posicionamiento</Chip>
          <h2 className="font-outfit text-3xl md:text-4xl font-semibold text-brand-primary mt-4 mb-6">
            Que Google (y ChatGPT) te encuentren
          </h2>
          <p className="text-brand-ink/85 leading-relaxed text-lg">
            La web está trabajada para posicionarse bien en Google en las búsquedas que importan: <em>"viajes
            de esquí en familia", "esquiar con niños desde Madrid", "Cerler familia"</em> y muchas más. Hemos
            hecho todo lo que Google exige hoy para dar prioridad a una web: velocidad, versión móvil impecable,
            estructura clara y las señales que Google usa para entender que sois una <strong>agencia
            especializada en viajes de esquí familiares con base en Madrid</strong>.
          </p>

          <div className="grid md:grid-cols-2 gap-5 mt-8">
            <PillarCard icon={MapPin} title="Google + Google Maps" index={0}>
              <p>
                Posicionamiento local en Madrid con datos de negocio, horarios y preparado para tu ficha
                de Google Maps. El objetivo: aparecer cuando alguien busca "agencia de esquí en Madrid".
              </p>
            </PillarCard>
            <PillarCard icon={Sparkles} title="ChatGPT, Perplexity, IA" index={1}>
              <p>
                Damos un paso más: la web también está preparada para que os citen los buscadores con
                inteligencia artificial. Cada vez más gente busca así, y estar preparados hoy es una
                ventaja competitiva importante.
              </p>
            </PillarCard>
          </div>
        </motion.div>
      </Section>

      {/* ---------- 4. BLOG ---------- */}
      <Section id="blog" className="bg-white/60 border-y border-brand-line">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}>
          <Chip>04 · Contenidos</Chip>
          <h2 className="font-outfit text-3xl md:text-4xl font-semibold text-brand-primary mt-4 mb-6">
            Un blog que también vende
          </h2>
          <p className="text-brand-ink/85 leading-relaxed text-lg">
            El blog no es de rellenar por rellenar. Cada artículo responde a una duda concreta que se hacen
            las familias justo antes de reservar, y las lleva de la mano hacia el viaje que mejor encaja con
            lo que buscan.
          </p>

          <div className="mt-8 grid gap-4">
            <div className="card-soft p-5 flex items-start gap-4">
              <BookOpen className="h-5 w-5 text-brand-primary shrink-0 mt-1" />
              <div>
                <p className="text-xs uppercase tracking-wider text-brand-muted mb-1">Ya publicados</p>
                <ul className="space-y-1 text-brand-ink/85">
                  <li>· Guía comparativa de estaciones para familias</li>
                  <li>· Comparativa Cerler vs Masella</li>
                  <li>· Cuánto cuesta un viaje de esquí en familia</li>
                </ul>
              </div>
            </div>
            <div className="card-soft p-5 flex items-start gap-4 bg-brand-primary/[0.03] border-brand-primary/20">
              <Sparkles className="h-5 w-5 text-brand-accent shrink-0 mt-1" />
              <div>
                <p className="text-xs uppercase tracking-wider text-brand-muted mb-1">Próximos</p>
                <ul className="space-y-1 text-brand-ink/85">
                  <li>· Qué llevar en la maleta para esquiar con niños (con checklist descargable)</li>
                  <li>· FAQs por temporada y guías por nivel</li>
                </ul>
              </div>
            </div>
          </div>

          <p className="text-brand-muted italic mt-6">
            Con el tiempo, el blog trae <strong>tráfico gratis todos los meses</strong> y se convierte en una
            fuente estable de leads sin depender solo de la publicidad.
          </p>
        </motion.div>
      </Section>

      {/* ---------- 5. MEDICIÓN CAMPAÑAS ---------- */}
      <Section id="campanas">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}>
          <Chip tone="accent">05 · Campañas</Chip>
          <h2 className="font-outfit text-3xl md:text-4xl font-semibold text-brand-primary mt-4 mb-6">
            Cada euro invertido, medido y optimizado
          </h2>
          <p className="text-brand-ink/85 leading-relaxed text-lg">
            Hemos dejado la web preparada para que <strong>cada visita, cada clic y cada formulario enviado
            se atribuya correctamente</strong> a la campaña que lo trajo, sea Google o Meta.
          </p>

          <div className="mt-8 card-soft p-6 md:p-8 bg-gradient-to-br from-brand-primary/[0.04] to-brand-accent/[0.04]">
            <h3 className="font-outfit text-xl font-semibold text-brand-primary flex items-center gap-2">
              <Zap className="h-5 w-5" /> El truco: doble medición
            </h3>
            <p className="mt-3 text-brand-ink/85 leading-relaxed">
              Hoy, los móviles de Apple, los bloqueadores de anuncios y el modo navegación privada hacen que
              un porcentaje muy alto de conversiones se pierda si sólo usas la medición clásica. Por eso hemos
              montado <strong>doble capa</strong>: por un lado la medición clásica desde el navegador, por otro
              un envío directo desde nuestros servidores a Google y a Meta.
            </p>
            <p className="mt-4 text-brand-ink/90 font-medium">
              Resultado → recuperamos las conversiones que otras agencias pierden, el algoritmo aprende más
              rápido y <span className="text-brand-primary">baja el coste por lead</span>.
            </p>
          </div>

          <p className="mt-6 text-brand-ink/85 leading-relaxed">
            Traducido al lanzamiento de mañana: <strong>desde el minuto uno</strong>, las creatividades que
            preparaste van a rendir mejor de lo que rendirían con una configuración estándar.
          </p>
        </motion.div>
      </Section>

      {/* ---------- 6. PANEL ---------- */}
      <Section id="panel" className="bg-white/60 border-y border-brand-line">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}>
          <Chip>06 · Tu panel</Chip>
          <h2 className="font-outfit text-3xl md:text-4xl font-semibold text-brand-primary mt-4 mb-6">
            Todo en un vistazo, sin depender de terceros
          </h2>
          <p className="text-brand-ink/85 leading-relaxed text-lg">
            Tienes tu propio panel de gestión donde ves cada lead que os llega y podéis hacer seguimiento en
            equipo.
          </p>

          <ul className="mt-8 space-y-4">
            <Bullet>
              Cada lead con su <strong>teléfono, viaje de interés, fechas y mensaje</strong>.
            </Bullet>
            <Bullet>
              Estados para hacer seguimiento: <em>nuevo, contactado, interesado, reservado, descartado</em>.
            </Bullet>
            <Bullet>
              Notas internas por lead para que el equipo comparta información.
            </Bullet>
            <Bullet>Filtros por viaje y por estado para saber, por ejemplo, cuántos leads de Cerler Navidad están sin contactar.</Bullet>
            <Bullet>Gestión de la configuración de las campañas sin depender de nadie.</Bullet>
          </ul>
        </motion.div>
      </Section>

      {/* ---------- RESUMEN ---------- */}
      <Section id="resumen">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}>
          <Chip>Resumen</Chip>
          <h2 className="font-outfit text-3xl md:text-4xl font-semibold text-brand-primary mt-4 mb-8">
            Dónde estamos hoy
          </h2>

          <ul className="space-y-4">
            <li className="flex items-start gap-3 p-4 rounded-2xl bg-emerald-50 border border-emerald-200">
              <span className="text-2xl leading-none">🟢</span>
              <span className="text-emerald-900 leading-relaxed"><strong>Google Ads</strong> ya rueda.</span>
            </li>
            <li className="flex items-start gap-3 p-4 rounded-2xl bg-amber-50 border border-amber-200">
              <span className="text-2xl leading-none">🚀</span>
              <span className="text-amber-900 leading-relaxed">
                <strong>Meta arranca mañana 1 de septiembre</strong> con las creatividades acordadas y todo el motor de medición afinado.
              </span>
            </li>
            <Bullet>Web nueva, moderna y pensada para convertir familias.</Bullet>
            <Bullet>Asistente 24/7 que resuelve dudas y empuja al lead.</Bullet>
            <Bullet>SEO trabajado para Google y para las IAs.</Bullet>
            <Bullet>Blog activo que trae tráfico gratis mes a mes.</Bullet>
            <Bullet>Panel propio para gestionar leads y campañas sin depender de terceros.</Bullet>
          </ul>
        </motion.div>
      </Section>

      {/* ---------- SIGUIENTE ---------- */}
      <Section id="siguiente" className="bg-brand-primary text-white">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}>
          <p className="uppercase tracking-wider text-xs text-white/70 mb-3 font-medium">Próximos pasos</p>
          <h2 className="font-outfit text-3xl md:text-4xl font-semibold mb-8">
            Cuando quieras, seguimos subiendo la nota
          </h2>

          <ul className="space-y-4">
            {[
              "Avisos por email en cuanto entra un lead, para reaccionar en caliente.",
              'Guía descargable ("Top 10 consejos para esquiar con niños") que capte emails de familias que todavía no están listas para reservar.',
              "Calendario editorial mensual del blog.",
              "Optimización de tu ficha de Google Maps para captar más búsquedas locales en Madrid.",
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-3">
                <ArrowRight className="h-5 w-5 shrink-0 mt-0.5 text-white/80" />
                <span className="text-white/95 leading-relaxed">{item}</span>
              </li>
            ))}
          </ul>

          <div className="mt-12 pt-8 border-t border-white/15">
            <p className="text-white/85 leading-relaxed">
              Cualquier duda me dices y lo vemos con calma. Mañana, atentos al lanzamiento 🙌
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-brand-primary font-medium hover:bg-white/90 transition-colors"
                data-testid="cta-ver-web"
              >
                <Globe className="h-4 w-4" /> Ver la web
              </Link>
              <a
                href="/blog"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/30 text-white hover:bg-white/10 transition-colors"
                data-testid="cta-ver-blog"
              >
                <BookOpen className="h-4 w-4" /> Ver el blog
              </a>
            </div>
          </div>
        </motion.div>
      </Section>

      {/* ---------- FOOTER MÍNIMO ---------- */}
      <footer className="py-10 text-center text-sm text-brand-muted border-t border-brand-line bg-white">
        <p>Club Amistad · Resumen ejecutivo preparado para Alberto</p>
      </footer>
    </div>
  );
}
